/**
 * draftSuppressionState.ts
 *
 * Module-level singleton state for "should we suppress composer
 * rehydration right now?" — shared between every component that owns
 * a composer that reads the global CHAT_DRAFT localStorage key
 * (useGlobalChat.ts, LandingPage.tsx, and any future consumer).
 *
 * BACKGROUND
 * ----------
 * The original composer-repop fix (Malaika 2026-05-14, PRs #130, #153,
 * 18500535, e95c2ebe) introduced a 1500 ms post-send suppression
 * window in useGlobalChat.ts (see `isWithinJustSentWindow` /
 * `JUST_SENT_DRAFT_SUPPRESS_MS`). That window was sized to cover the
 * worst-case adjacent-component 500 ms debounce + the 1000 ms
 * cross-tab poll cadence.
 *
 * The window is too small for Deep Research turns, which legitimately
 * run 5-8 minutes end-to-end. By the time the SSE / task-poll response
 * lands the 1500 ms window has long expired AND the OTHER composer
 * (LandingPage.tsx hero composer) — which has its own draft
 * persistence/restore loop with NO send-window guard at all — can
 * rewrite the just-sent text back into the composer.
 *
 * The structural fix shipped here adds two new gates to the existing
 * 1500 ms gate:
 *
 *   1. "in-flight" — while ANY consumer has marked a turn in-flight
 *      via markProcessingStarted(), suppression is unconditional. The
 *      consumer is responsible for calling markProcessingEnded() when
 *      the turn completes (success, error, or cancel).
 *
 *   2. "post-flight cooldown" — for 1500 ms AFTER the last
 *      markProcessingEnded(), suppression remains active. This covers
 *      the same race the original 1500 ms window covered, except now
 *      anchored to turn-end instead of just send-start: any debounced
 *      persist effect that was queued during the long turn (e.g. the
 *      LandingPage 500 ms persist debounce holding stale state from
 *      300 ms before the send) gets a guaranteed 1500 ms window after
 *      processing-end during which the poll/storage-listener paths
 *      will NOT rehydrate.
 *
 * RESULT
 * ------
 * The composer cannot be rehydrated with the just-sent text for the
 * ENTIRE duration of an in-flight turn, regardless of turn length
 * (1 ms or 8 minutes). The existing 1500 ms post-send window is
 * preserved as a safety net for synchronous (fire-and-forget) sends
 * that never set isProcessing=true and never call markProcessingStarted
 * (e.g. unauthenticated LandingPage prompts that surface a
 * construction-waitlist modal).
 *
 * The module is intentionally framework-agnostic — no React, no
 * window globals — so it is trivially unit-testable.
 */

// ============================================================================
// Tunables
// ============================================================================

/**
 * Default "just-sent" suppression window — matches the historical
 * `JUST_SENT_DRAFT_SUPPRESS_MS` constant in useGlobalChat.ts. Sized
 * to cover the adjacent-component 500 ms debounce + 1000 ms poll
 * cadence + a small safety margin.
 */
export const JUST_SENT_SUPPRESS_MS = 1500;

/**
 * Post-flight cooldown — applied for this many ms AFTER
 * markProcessingEnded() fires. Sized the same as the just-sent
 * window because the same races (debounced persist from another
 * component) are in play.
 */
export const POST_FLIGHT_SUPPRESS_MS = 1500;

/**
 * Window during which a `lastSentText` sentinel is honoured as a
 * "reject this write — its closure carries the just-sent text"
 * gate. Sized generously (60 s) so even a wildly delayed debounce
 * scheduled DURING typing of a 1200-char prompt cannot resurrect
 * the just-sent text into the draft store. The sentinel does NOT
 * itself stamp the time-window suppression — it is a content-domain
 * gate complementary to the time-domain gates above.
 *
 * FH-QA composer-refill race on long NON-attachment prompts
 * (2026-05-21 16:25 UTC repro): a 1200-char demand-letter prompt
 * typed and sent without attachments still surfaced a stale
 * debounced save firing on the tail of the message after RESET_PROMPT.
 * The time-domain gates above were already in place but the closure
 * fired in a window-edge case the time math did not catch — see
 * `composerRefillNonAttachmentRace.test.ts` for the timeline.
 */
export const SENT_TEXT_SENTINEL_MS = 60_000;

// ============================================================================
// Module-level state
// ============================================================================

interface DraftSuppressionState {
  lastSendAt: number;
  inFlightCount: number;
  lastProcessingEndedAt: number;
  /** Text of the most recently sent message (plain). 0-length when unset. */
  lastSentText: string;
  /** HTML of the most recently sent message. 0-length when unset. */
  lastSentHtml: string;
  /** Clock time `lastSentText`/`lastSentHtml` was stamped. */
  lastSentTextAt: number;
}

const state: DraftSuppressionState = {
  lastSendAt: 0,
  inFlightCount: 0,
  lastProcessingEndedAt: 0,
  lastSentText: '',
  lastSentHtml: '',
  lastSentTextAt: 0,
};

// ============================================================================
// Public mutators
// ============================================================================

/**
 * Stamp the post-send window open at `now`. Call from handleSend (any
 * composer) at the synchronous start of the send path, BEFORE
 * clearing prompt state, so any draft-rehydrate path racing against
 * the synchronous clear sees the window and skips.
 *
 * @param now — clock value (Date.now()). Parameterised for testability.
 */
export function markSentNow(now: number = Date.now()): void {
  state.lastSendAt = now;
}

/**
 * Mark a turn as in-flight. Increments a refcount so concurrent turns
 * (e.g. two threads streaming simultaneously, or back-to-back sends
 * where a second startProcessing fires before the first stops) compose
 * correctly: suppression stays active until EVERY started turn has
 * been ended.
 *
 * Idempotent per-call: each markProcessingStarted MUST be paired with
 * exactly one markProcessingEnded. Callers that flip
 * `state.isProcessing` reactively should wire this through a
 * useEffect that only fires on the transition.
 */
export function markProcessingStarted(): void {
  state.inFlightCount += 1;
}

/**
 * Pair to markProcessingStarted. Decrements the refcount and stamps
 * the post-flight cooldown window open at `now`. If the refcount is
 * already 0, the call is a no-op except for the cooldown stamp — this
 * defensiveness covers the case where a consumer drops/misses a
 * markProcessingStarted (e.g. on a stuck-state auto-clear path) and
 * later calls markProcessingEnded.
 *
 * @param now — clock value (Date.now()). Parameterised for testability.
 */
export function markProcessingEnded(now: number = Date.now()): void {
  if (state.inFlightCount > 0) {
    state.inFlightCount -= 1;
  }
  state.lastProcessingEndedAt = now;
}

/**
 * Stamp the just-sent text/html as a content-domain sentinel. Any
 * debounced save closure that fires later — inside or outside the
 * time-domain suppression window — and whose captured prompt text
 * matches the sentinel will be rejected by `isSentTextMatch()`.
 *
 * Pair to `markSentNow`. Call from every composer's send wrapper at
 * the synchronous start of the send path, BEFORE clearing prompt
 * state, so any draft-rehydrate path racing against the synchronous
 * clear has both gates available.
 *
 * Empty strings are a no-op (we never want to silently suppress
 * legitimately empty drafts).
 *
 * @param text  — plain-text body just submitted to the turn pipeline.
 * @param html  — optional HTML form of the same body.
 * @param now   — clock value (Date.now()). Parameterised for testability.
 */
export function markSentText(
  text: string,
  html: string = '',
  now: number = Date.now(),
): void {
  if (!text && !html) return;
  state.lastSentText = text || '';
  state.lastSentHtml = html || '';
  state.lastSentTextAt = now;
}

/**
 * Whitespace-insensitive comparable form of a body: runs of whitespace
 * (incl. &nbsp;) collapse to single spaces, edges trimmed.
 *
 * WHY normalized, not exact (2026-08-12 sticky-composer round): the
 * sentinel is stamped from handleSend's TRIMMED/normalized `text` +
 * `safeHtml`, but every save/rehydrate payload carries the RAW composer
 * body — instrumented in production-bundle repro as
 * `"…probe message "` (trailing space) vs sentinel `"…probe message"`.
 * Exact comparison therefore missed precisely the payloads the sentinel
 * exists to reject, and the just-sent text resurrected into the
 * composer. Collapsing whitespace cannot mask a legitimately different
 * later draft: content that differs only by whitespace from a message
 * sent <60 s ago IS the resurrection, not new input.
 */
export function normalizeForSentMatch(body: string): string {
  return (body || '').replace(/\u00A0/g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * Crude tag-stripper for HTML equality checks — enough to compare two
 * bodies from the SAME composer pipeline (contentEditable innerHTML vs
 * normalizeChatHtml output). Framework-free on purpose; this is a
 * comparison aid, not a sanitizer.
 */
export function htmlToComparableText(html: string): string {
  return normalizeForSentMatch(
    (html || '')
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<\/(p|div|li|h[1-6])>/gi, ' ')
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>'),
  );
}

/**
 * Returns true when:
 *   - the `text`/`html` argument matches the last-sent sentinel stamped
 *     via `markSentText` under whitespace-insensitive comparison, AND
 *   - we are within `SENT_TEXT_SENTINEL_MS` of that stamp.
 *
 * Comparison is NORMALIZED (see normalizeForSentMatch): the sentinel is
 * stamped from the send path's trimmed/normalized shapes while save and
 * rehydrate payloads carry the raw composer body, so exact-string
 * matching structurally missed real resurrections (2026-08-12 repro).
 * Text and html are cross-compared through their comparable text forms
 * so a text-only stamp still rejects an html payload of the same body.
 *
 * Pure — does not mutate state.
 *
 * @param text  — plain-text candidate the caller wants to persist.
 * @param html  — optional HTML candidate.
 * @param now   — clock value (Date.now()). Parameterised for testability.
 */
export function isSentTextMatch(
  text: string,
  html: string = '',
  now: number = Date.now(),
): boolean {
  if (!state.lastSentTextAt) return false;
  const age = now - state.lastSentTextAt;
  if (age < 0 || age >= SENT_TEXT_SENTINEL_MS) return false;

  const sentText = normalizeForSentMatch(state.lastSentText);
  const sentHtmlText = state.lastSentHtml ? htmlToComparableText(state.lastSentHtml) : '';
  const candText = normalizeForSentMatch(text);
  const candHtmlText = html ? htmlToComparableText(html) : '';

  // Match if ANY comparable pairing lines up. A composer might persist
  // its closure-captured html while another composer's send stamped only
  // text (or vice versa); we honour every combination.
  if (candText && sentText && candText === sentText) return true;
  if (candHtmlText && sentHtmlText && candHtmlText === sentHtmlText) return true;
  if (candHtmlText && sentText && candHtmlText === sentText) return true;
  if (candText && sentHtmlText && candText === sentHtmlText) return true;
  // Preserve the historical exact-html match as well (zero-risk keep).
  if (html && state.lastSentHtml && html === state.lastSentHtml) return true;
  return false;
}

/**
 * Test-only / SSR-only reset. Wipes all module state back to the
 * zero-init. Not part of the production call surface — production
 * code should never reset this; the suppression flags are designed
 * to be append-only (sends accumulate, processing refcount drains
 * naturally).
 */
export function __resetDraftSuppressionStateForTests(): void {
  state.lastSendAt = 0;
  state.inFlightCount = 0;
  state.lastProcessingEndedAt = 0;
  state.lastSentText = '';
  state.lastSentHtml = '';
  state.lastSentTextAt = 0;
}

// ============================================================================
// Public queries (pure)
// ============================================================================

/**
 * Returns true if `now` falls inside the post-send suppression window
 * opened at `lastSendAt`. Pure — does not read module state.
 *
 * Exported separately so the existing per-window test in
 * composerRepopSuppress.test.ts (which imports `isWithinJustSentWindow`
 * from useGlobalChat.ts) can continue to exercise the same semantics
 * via that re-export.
 */
export function isWithinJustSentWindowPure(
  lastSendAt: number,
  now: number,
  windowMs: number = JUST_SENT_SUPPRESS_MS,
): boolean {
  if (!lastSendAt) return false;
  const delta = now - lastSendAt;
  return delta >= 0 && delta < windowMs;
}

/**
 * THE authoritative "should we suppress composer rehydration?" gate.
 * Returns true when ANY of the following holds:
 *
 *   - within JUST_SENT_SUPPRESS_MS of the last markSentNow(); OR
 *   - any consumer currently has a turn in-flight
 *     (inFlightCount > 0); OR
 *   - within POST_FLIGHT_SUPPRESS_MS of the last
 *     markProcessingEnded().
 *
 * Returns false otherwise — i.e. legitimate cross-tab restore (the
 * scenario the original test scenario 2 guards) still fires after the
 * post-flight cooldown elapses.
 *
 * @param now — clock value (Date.now()). Parameterised for testability.
 */
export function shouldSuppressDraftRehydrate(
  now: number = Date.now(),
): boolean {
  // In-flight turn — unconditional suppression for the entire turn
  // duration, regardless of how long it runs (5 ms or 8 min).
  if (state.inFlightCount > 0) return true;

  // Post-send window (legacy 1500 ms guard, preserved as safety net
  // for fire-and-forget sends that never flip isProcessing).
  if (isWithinJustSentWindowPure(state.lastSendAt, now)) return true;

  // Post-flight cooldown — covers debounced persist effects that
  // queued state from BEFORE the turn ended.
  if (state.lastProcessingEndedAt) {
    const delta = now - state.lastProcessingEndedAt;
    if (delta >= 0 && delta < POST_FLIGHT_SUPPRESS_MS) return true;
  }

  return false;
}

/**
 * Inspector for tests / debug overlays. Returns a snapshot of the
 * module-level state. Not part of the production call surface.
 */
export function __getDraftSuppressionStateForTests(): Readonly<DraftSuppressionState> {
  return { ...state };
}
